import { localizedStrings } from '@core/strings'
import {
  SelectFilterInternalArguments,
  SelectFilterValue,
  useSelectFilter,
} from './fundamentals'
import { FilterHookArgumentsBase } from '@core/providers/filters'
import Utils from '@core/utils/utils'
import { PermitStatus } from '@core/entities/permit/PermitBase'
import { useMemo } from 'react'

type PermitStatusFilter<Multiple extends boolean | undefined> = Omit<
  FilterHookArgumentsBase<SelectFilterValue<PermitStatus, Multiple>>,
  'label'
> & {
  // add label as an optional param only for status
  label?: string
  multiple?: Multiple
  whitelist?: PermitStatus[]
}

export const PermitStatusFilterOptions: PermitStatus[] = [
  'REQUESTED',
  'SENT_TO_PERMIT_HOLDER',
  'ISSUED',
  'OPEN',
  'EXTENSION_REQUESTED',
  'SUSPENDED',
  'EXPIRED',
  'AWAITING_CHECKS',
  'AWAITING_FINAL_SIGN_OFF',
  'CLOSED',
  'REJECTED',
  'DISCARDED',
  'CANCELLED',
]

export const permitStatusFilterValueIsPartial = (
  value: Array<PermitStatus>,
) => {
  if (!value?.length) {
    return true
  }
  return false
}

export const usePermitStatusFilter: <Multiple extends boolean | undefined>(
  arg: PermitStatusFilter<Multiple>,
) => SelectFilterInternalArguments<PermitStatus, Multiple> = ({
  label = localizedStrings.status,
  ...args
}) => {
  const filteredStatuses = useMemo(() => {
    if (args?.whitelist) {
      return PermitStatusFilterOptions.filter((status: PermitStatus) =>
        args?.whitelist.includes(status),
      )
    }
    return PermitStatusFilterOptions
  }, [args?.whitelist])

  return useSelectFilter({
    ...args,
    label,
    options: filteredStatuses,
    valueToLabel: (v) => Utils.getTextForStatus(v),
    multiple: args.multiple,
    dataTest: 'statuses-filter',
  })
}
